import React, { useState } from "react";
import { Header } from "../../../../../components";
import {
  Button,
  Input,
  InputNumber,
  Table,
  Modal,
  Form,
  Space,
  Switch,
  Tag,
  Popconfirm,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  useErrorTopics,
  useCreateErrorTopic,
  useUpdateErrorTopic,
  useDeleteErrorTopic,
} from "../../../../../hooks/useErrorTopic";

function ErrorTopicPage() {
  const queryClient = useQueryClient();
  const { data: topics = [], isLoading } = useErrorTopics();
  const { mutateAsync: createTopic, isLoading: isCreating } = useCreateErrorTopic();
  const { mutateAsync: updateTopic, isLoading: isUpdating } = useUpdateErrorTopic();
  const { mutateAsync: deleteTopic } = useDeleteErrorTopic();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const refresh = () => queryClient.invalidateQueries(["errorTopics"]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ topic: record.topic, sort_order: record.sort_order });
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await updateTopic({
          id: editing.id,
          topic: values.topic,
          sort_order: values.sort_order,
        });
        message.success("แก้ไขหัวข้อสำเร็จ");
      } else {
        await createTopic({ topic: values.topic, sort_order: values.sort_order });
        message.success("เพิ่มหัวข้อสำเร็จ");
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditing(null);
      refresh();
    } catch (error) {
      if (error?.errorFields) return; // validation ของ antd
      await message.destroy();
      message.error(error?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleToggleEnable = async (record, checked) => {
    try {
      await updateTopic({ id: record.id, enable: checked ? 1 : 0 });
      refresh();
    } catch (error) {
      await message.destroy();
      message.error("เปลี่ยนสถานะไม่สำเร็จ");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTopic(id);
      message.success("ลบหัวข้อสำเร็จ");
      refresh();
    } catch (error) {
      await message.destroy();
      message.error("ลบไม่สำเร็จ");
    }
  };

  const columns = [
    {
      title: "ลำดับ",
      dataIndex: "sort_order",
      key: "sort_order",
      align: "center",
      width: 90,
    },
    { title: "หัวข้อเคสผิดพลาด", dataIndex: "topic", key: "topic" },
    {
      title: "สถานะ",
      key: "enable",
      align: "center",
      width: 160,
      render: (_, record) => (
        <Space>
          <Switch
            size="small"
            checked={Number(record.enable) === 1}
            onChange={(checked) => handleToggleEnable(record, checked)}
          />
          {Number(record.enable) === 1 ? (
            <Tag color="green">ใช้งาน</Tag>
          ) : (
            <Tag>ปิด</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "จัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            แก้ไข
          </Button>
          <Popconfirm
            title="ยืนยันการลบหัวข้อนี้?"
            description="เคสเก่าที่เลือกหัวข้อนี้ไว้จะยังแสดงข้อความเดิม"
            onConfirm={() => handleDelete(record.id)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button size="small" danger>
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="md:m-10">
      <div className="flex justify-between items-center mb-6">
        <Header category="Admin" title="หัวข้อเคสผิดพลาด" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          เพิ่มหัวข้อ
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={topics}
        columns={columns}
        pagination={false}
        bordered
      />

      <Modal
        title={editing ? "แก้ไขหัวข้อเคสผิดพลาด" : "เพิ่มหัวข้อเคสผิดพลาด"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => {
          setIsModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        okText="บันทึก"
        cancelText="ยกเลิก"
        okButtonProps={{ loading: isCreating || isUpdating }}
        confirmLoading={isCreating || isUpdating}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="topic"
            label="หัวข้อ"
            rules={[{ required: true, message: "กรุณากรอกหัวข้อ" }]}
          >
            <Input placeholder="เช่น เวลาสลิปผิด, เติมผิดยูส" />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="ลำดับการแสดงผล"
            tooltip="เลขน้อยอยู่บน — เว้นว่างไว้จะต่อท้ายรายการให้อัตโนมัติ"
          >
            <InputNumber min={0} step={1} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ErrorTopicPage;
